---
title: Agent Skill
description: Give your coding agent the audio/ui skill so it picks the right component, wires it to Web Audio without clicks, keeps live data out of React and styles the primitives.
---

The audio/ui skill gives a coding agent the library's rules: which layer and component fit a task, how to install them, how to wire them to the Web Audio API, how to feed meters and playheads without re-rendering React, how to style the headless primitives, and how to let an AI model drive them.

## Install the skill

Works with Claude Code, Cursor, Codex and any agent that supports the open skills format.

```bash
npx skills add ouestlabs/audio-ui
```

### In Claude Code, as a plugin

The repository is also a Claude Code plugin marketplace, so Claude Code installs and updates the skills itself:

```text
/plugin marketplace add ouestlabs/audio-ui
/plugin install audio-ui@audio-ui
```

On Claude Code 2.1.275 or later, one line does both: `/plugin install audio-ui --marketplace ouestlabs/audio-ui`. The plugin holds four skills and no hook, command or server, and it follows the repository: `/plugin marketplace update audio-ui` brings the latest rules.

The agent loads each skill on its own when a task needs it, so a Vue knob does not pay for the design skins. Their rules come with incorrect and correct code for each case:

| Skill | Rules | What they cover |
| --- | --- | --- |
| `audio-ui` | Values and events, accessibility, styling, AI control | Picking and installing components, controlled values, change versus commit, accessible names, registry props and tokens, typed tools a model calls to compose, edit and mix |
| `web-audio` | Web Audio, live data | Starting the audio context, ramping parameters, dB to gain, scheduling on the audio clock, meters, spectra, waveforms and playheads fed by a source, without a render per frame |
| `design` | Design | Retro, minimal and modern skins for the headless primitives, and composing a look from a brief |
| `core` | Without React | Building the controls for Vue, Svelte, web components or the plain DOM on the framework-free controllers of `@audio-ui/core` |

The skills live in the [audio-ui repository](https://github.com/ouestlabs/audio-ui/tree/main/skills), and CI checks that every name they mention exists in the library. To install them by hand, copy the folders of `skills/` into your agent's skills directory (for Claude Code, `.claude/skills/`).

## What it does

Once installed, the agent uses it whenever you ask for a knob, fader, XY pad, meter, waveform, timeline, sequencer, piano roll or any synth, mixer or DAW interface in React. It will:

1. Read your project to see if it uses shadcn/ui, plain React or another framework, then choose the registry, `@audio-ui/react` or `@audio-ui/core`.
2. Find the right component and read its docs before writing code.
3. Install it with your package manager.
4. Connect the control to your audio code the safe way, without clicks while dragging, and feed live displays without re-rendering React.
5. Check keyboard access and accessible names.

## Add the MCP server

The skill works best next to the [MCP server](/docs/mcp), which lets the agent look up components and docs live instead of relying on memory.

## Example prompts

- Build a synth control panel with Knob, XY Pad and Fader.
- Add an equalizer with a Plot and draggable band handles.
- Make a piano roll with a keyboard, a velocity lane and a loop range.
- Show the live level of my microphone on a stereo meter.
- Style a knob with my own CSS, without Tailwind.
- Let the chat write a bassline into the piano roll.
